/**
 * OIDC-Login (Entra ID) für die App selbst — Authorization Code + PKCE,
 * ohne Client-Secret (SPA). Das Access-Token trägt unsere API als Audience
 * und wird vom Backend stateless via JWKS geprüft.
 *
 * Ablauf: /api/v1/auth-config sagt, ob OIDC aktiv ist (+ Tenant/Client-ID).
 * Ohne gültiges Token → Redirect zu Entra; Callback (?code=…) wird gegen den
 * Token-Endpoint getauscht (CORS für SPA-Redirects erlaubt Entra explizit).
 */

interface AuthConfig {
  enabled: boolean;
  tenant_id: string;
  client_id: string;
}

const TOKEN_KEY = "tbs.access_token";
const IDTOKEN_KEY = "tbs.id_token";
const EXPIRY_KEY = "tbs.token_expiry";
const VERIFIER_KEY = "tbs.pkce_verifier";
const STATE_KEY = "tbs.oauth_state";

let cfg: AuthConfig | null = null;

async function loadConfig(): Promise<AuthConfig> {
  if (!cfg) {
    cfg = (await fetch("/api/v1/auth-config").then((r) => r.json())) as AuthConfig;
  }
  return cfg;
}

/** Aktuelles Access-Token, oder null wenn keins/abgelaufen. */
export function accessToken(): string | null {
  const t = sessionStorage.getItem(TOKEN_KEY);
  const exp = Number(sessionStorage.getItem(EXPIRY_KEY) ?? 0);
  if (!t || Date.now() > exp - 30_000) return null; // 30s Puffer
  return t;
}

function b64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function sha256(s: string): Promise<Uint8Array> {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return new Uint8Array(d);
}

/** Startet den Login-Redirect zu Entra (PKCE). */
export async function login(): Promise<void> {
  const c = await loadConfig();
  if (!c.enabled) return;
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  const p = new URLSearchParams({
    client_id: c.client_id,
    response_type: "code",
    redirect_uri: window.location.origin + "/",
    response_mode: "query",
    scope: `api://${c.client_id}/access openid profile`,
    state,
    code_challenge: b64url(await sha256(verifier)),
    code_challenge_method: "S256",
  });
  window.location.assign(
    `https://login.microsoftonline.com/${c.tenant_id}/oauth2/v2.0/authorize?${p}`,
  );
}

/**
 * Tauscht den ?code=-Callback gegen ein Token. true = angemeldet, false =
 * kein Callback in der URL. Ein FEHLGESCHLAGENER Callback wirft — sonst
 * leitet initAuth sofort wieder zu Entra um und bei einem dauerhaften Fehler
 * (falsche Redirect-URI, fehlende App-Zuweisung) entsteht eine Endlosschleife.
 */
async function handleCallback(): Promise<boolean> {
  const q = new URLSearchParams(window.location.search);
  const code = q.get("code");
  const err = q.get("error");
  if (!code && !err) return false;
  // Callback-Parameter sofort aus der URL (auch im Fehlerfall: ein Reload
  // soll neu anmelden statt denselben Code nochmal einzulösen).
  window.history.replaceState({}, "", window.location.pathname);
  const state = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  if (err || !code) throw new Error(`Entra-Login: ${err} — ${q.get("error_description") ?? ""}`);
  if (!state || !verifier || q.get("state") !== state) {
    throw new Error("Entra-Login: state stimmt nicht (Callback nicht von diesem Tab angefordert)");
  }
  const c = await loadConfig();
  const body = new URLSearchParams({
    client_id: c.client_id,
    grant_type: "authorization_code",
    code,
    redirect_uri: window.location.origin + "/",
    code_verifier: verifier,
  });
  const resp = await fetch(
    `https://login.microsoftonline.com/${c.tenant_id}/oauth2/v2.0/token`,
    { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body },
  );
  if (!resp.ok) {
    throw new Error(`Entra-Login: Token-Tausch fehlgeschlagen (${resp.status}) ${await resp.text()}`);
  }
  const tok = (await resp.json()) as { access_token: string; id_token?: string; expires_in: number };
  sessionStorage.setItem(TOKEN_KEY, tok.access_token);
  if (tok.id_token) sessionStorage.setItem(IDTOKEN_KEY, tok.id_token);
  sessionStorage.setItem(EXPIRY_KEY, String(Date.now() + tok.expires_in * 1000));
  return true;
}

/** Angemeldeter User (aus dem ID-Token; Fallback Access-Token). */
export interface AuthUser {
  name: string;
  upn: string;
}

export function currentUser(): AuthUser | null {
  const raw = sessionStorage.getItem(IDTOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    // Nur Anzeige — die Validierung passiert serverseitig (JWKS).
    const payload = JSON.parse(
      atob(raw.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>;
    const upn = String(payload.preferred_username ?? payload.upn ?? "");
    const name = String(payload.name ?? upn);
    if (!name && !upn) return null;
    return { name, upn };
  } catch {
    return null;
  }
}

/** Abmelden: lokale Session verwerfen + Entra-Sign-out (beendet die SSO-Session). */
export async function logout(): Promise<void> {
  const c = await loadConfig().catch(() => null);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(IDTOKEN_KEY);
  sessionStorage.removeItem(EXPIRY_KEY);
  if (c?.enabled) {
    const p = new URLSearchParams({ post_logout_redirect_uri: window.location.origin + "/" });
    window.location.assign(
      `https://login.microsoftonline.com/${c.tenant_id}/oauth2/v2.0/logout?${p}`,
    );
  } else {
    window.location.assign("/");
  }
}

/**
 * Beim App-Start aufrufen: verarbeitet einen Login-Callback und leitet zum
 * Login um, wenn OIDC aktiv ist und kein gültiges Token vorliegt.
 * Liefert true = App rendern; false = Login-Redirect läuft (nichts rendern,
 * sonst blitzt die Seite kurz auf, bevor der Browser navigiert). Wirft, wenn
 * der Login-Callback fehlschlug (Aufrufer zeigt den Fehler statt Redirect).
 */
export async function initAuth(): Promise<boolean> {
  const c = await loadConfig().catch(() => null);
  if (!c?.enabled) return true;
  if (await handleCallback()) return true;
  if (accessToken()) return true;
  await login(); // startet den Redirect — Navigation folgt asynchron
  return false;
}
